import React from 'react'
import PropTypes from 'prop-types'

import styled from '@emotion/styled'
import { ThemeProvider } from '@emotion/react'
import { useSelector } from 'react-redux'

import BaseWrapper from './BaseWrapper'
import MobileReset from './MobileReset'
import { selectIsWebView, selectCurrentTheme } from '../../../flux/selectors/ux'

const StyledBaseWrapper = styled(BaseWrapper)(() => ({
  display: 'flex',
  flexDirection: 'column',
}))

const Header = styled('div')()

const Main = styled('div')(() => ({
  position: 'relative',
  flex: '1 0 0px',
  overflowY: 'auto',
}))

const Footer = styled('div')(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  width: '100%',
  height: theme.spaces.default * 3.75,
  zIndex: 10,
}))

export const getLayoutHorizontalPadding = (withPadding, { breakpoints, spaces }) => {
  const {
    xlarge, small,
  } = breakpoints
  const space = spaces.default

  return {
    default: withPadding ? space / 2 : 0,
    [small]: withPadding ? space : 0,
    [xlarge]: withPadding ? space * 1.5 : 0,
  }
}

const Wrapper = styled('div')(({ withPadding, reduced, theme }) => {
  const {
    spaces: {
      default: space,
    },
    breakpoints: {
      xlarge: xLargeBreakpoint,
      xxlarge,
      xlarge,
      small: smallBreakpoint,
    },
  } = theme
  const hPadding = getLayoutHorizontalPadding(withPadding, theme)

  return {
    position: 'relative',
    width: '100%',
    margin: '0 auto',
    paddingTop: withPadding ? space : 0,
    paddingBottom: 0,

    paddingRight: hPadding.default,
    paddingLeft: hPadding.default,
    [`@media (min-width: ${smallBreakpoint})`]: {
      paddingRight: hPadding[smallBreakpoint],
      paddingLeft: hPadding[smallBreakpoint],
    },
    [`@media (min-width: ${xLargeBreakpoint})`]: {
      maxWidth: reduced ? xlarge : xxlarge,
      paddingRight: hPadding[xLargeBreakpoint],
      paddingLeft: hPadding[xLargeBreakpoint],
    },
  }
})

const renderBox = (children, boxType) => (
  React.Children.toArray(children).find(child => child.type === boxType)
)

const Layout = ({ children }) => {
  const isWebView = useSelector(selectIsWebView)
  const theme = useSelector(selectCurrentTheme)

  return (
    <ThemeProvider theme={theme}>
      <StyledBaseWrapper>
        {isWebView ? (<MobileReset />) : null}
        {renderBox(children, Header)}
        {renderBox(children, Main)}
        {renderBox(children, Footer)}
      </StyledBaseWrapper>
    </ThemeProvider>
  )
}

Layout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({}),
  ]),
}

Layout.defaultProps = {
  children: [],
}

Header.displayName = 'Header'
Main.displayName = 'Main'
Footer.displayName = 'Footer'
Wrapper.displayName = 'Wrapper'

Layout.Header = Header
Layout.Main = Main
Layout.Footer = Footer
Layout.Wrapper = Wrapper
Layout.getLayoutHorizontalPadding = getLayoutHorizontalPadding

export default Layout
